import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { prepareData } from 'helpers/prepare-req-data'
import { prepParams } from 'helpers/prep-req-url'
import { getRespData  } from 'helpers/get-resp-data'
import client from 'api'
import _ from 'lodash'

import { v4 as uuid4 } from 'uuid'


const initialState = {
    loadVertragListStatus: 'idle',
    loadVertragListError: undefined as any,
    loadVertragListLastArgs: undefined as any,
    loadVertragStatus: 'idle',
    loadVertragError: undefined as any,
    saveVertragStatus: 'idle',
    saveVertragError: undefined as any,
    deleteVertragStatus: 'idle',
    deleteVertragError: undefined as any,

    vertragList: [] as any[],
    formItem: undefined as any,
    filterValues: {} as any,
    filterLookupValues: {} as any,

    directLoad: false,
    load: false,
    activeDivRef: null as any,

    filterValuesId: null,
    valuesFromDB: false,
    filterVisibleId: null,
    filterVisible: true,
}


export const loadVertragList = createAsyncThunk('meldungVertragSlice/loadVertragList', async ({ parentModel, forceReload }: any, { rejectWithValue, getState }: any) => {
    const lastArgs = getState().meldungVertragSlice.loadVertragListLastArgs
    if (_.isEqual(lastArgs, { parentModel }) && !forceReload) { return }

    let promises = []
    promises.push(client.get(
        `/api/ixweb/nurisvertrag/?action=gte&TOID_FILTER=${parentModel.TOID}&FLSTMID_FILTER=${parentModel.FLSTMID}&OBJID_FILTER=${parentModel.OBJID}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: value1.map((x: any) => ({
            ...x,
        })),
        args: { parentModel }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadVertragListStatus } = getState().meldungVertragSlice
        return loadVertragListStatus !== 'pending'
    }
})

export const loadVertrag = createAsyncThunk('meldungVertragSlice/loadVertrag', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.get(
        `/api/ixweb/nurisvertrag/?action=eql&HDR_ID=${id}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadVertragStatus } = getState().meldungVertragSlice
        return loadVertragStatus !== 'pending'
    }
})

export const saveVertrag = createAsyncThunk('meldungVertragSlice/saveVertrag', async ({ vertrag }: any, { rejectWithValue }: any) => {
    let promises = []
    if (vertrag?.HDR_ID) {
        promises.push(client.put(
            `/api/ixweb/nurisvertrag/${ vertrag?.HDR_ID }/`,
            prepareData(vertrag),
        ))
    } else {
        promises.push(client.post(
            `/api/ixweb/nurisvertrag/`,
            prepareData(vertrag),
        ))
    }

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { saveVertragStatus } = getState().meldungVertragSlice
        return saveVertragStatus !== 'pending'
    }
})

export const deleteVertrag = createAsyncThunk('meldungVertragSlice/deleteVertrag', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.delete(
        `/api/ixweb/nurisvertrag/${id}/`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: id,
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { deleteVertragStatus } = getState().meldungVertragSlice
        return deleteVertragStatus !== 'pending'
    }
})

const VertragSlice = createSlice({
    name: 'meldungVertragSlice',
    initialState,
    reducers: {
        resetloadVertragListError: (state) => {
            state.loadVertragListError = undefined
        },
        resetloadVertrag: (state) => {
            state.formItem = undefined
            state.loadVertragError = undefined
        },
        resetloadVertragError: (state) => {
            state.loadVertragError = undefined
        },
        resetsaveVertrag: (state) => {
            state.formItem = undefined
            state.saveVertragError = undefined
        },
        resetsaveVertragError: (state) => {
            state.saveVertragError = undefined
        },
        resetdeleteVertragError: (state) => {
            state.deleteVertragError = undefined
        },
        removeFromVertragListById: (state, action) => {
            state.vertragList = state.vertragList.filter((x: any) => x.HDR_ID !== action.payload)
        },
        removeFromVertragListByIds: (state, action) => {
            state.vertragList = state.vertragList.filter((x: any) => action.payload.indexOf(x.HDR_ID) === -1)
        },
        setFilterValues: (state, action) => {
            if (action.payload === undefined || action.payload === null) {
                state.filterValues = {}
            } else {
                state.filterValues = { ...state.filterValues, ...action.payload }
            }
        },
        setFilterLookupValue: (state, action) => {
            const { name, value, formValue } = action.payload
            if (name) {
                state.filterLookupValues[name] = value
                state.filterValues[name] = formValue
            }
        },
        setDirectLoad: (state, action) => { state.directLoad = action.payload },
        setLoad: (state, action) => { state.load = action.payload },
        setActiveDivRef: (state, action) => { state.activeDivRef = action.payload},
        setFilterValuesId: (state, action) => {
            state.filterValuesId = action.payload
        },
        setValuesFromDB: (state, action) => {
            state.valuesFromDB = action.payload
        },
        setFilterVisibleId: (state, action) => {
            state.filterVisibleId = action.payload
        },
        setFilterVisible: (state, action) => {
            state.filterVisible = action.payload
        },
        preloadData: (state, action) => {
            state.vertragList = action.payload.map((x: any) => ({ ...x, HDR_ID: uuid4()}))
            state.loadVertragListStatus = 'preloaded'
            state.loadVertragListLastArgs = ''
        },
        clearPreloadedData: (state) => {
            state.vertragList = []
            state.loadVertragListStatus = 'idle'
            state.loadVertragListLastArgs = undefined
        },
    },
    extraReducers: builder => {
        builder.addCase(loadVertragList.pending, (state) => {
            state.loadVertragListStatus = 'pending'
            state.loadVertragListError = undefined
        })
        builder.addCase(loadVertragList.fulfilled, (state, action) => {
            state.loadVertragListStatus = 'idle'

            if (!action.payload) { return }

            state.loadVertragListLastArgs = action.payload.args

            {
                state.vertragList = action.payload.data

            }
        })
        builder.addCase(loadVertragList.rejected, (state, action) => {
            state.loadVertragListStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadVertragListError = action.error.message
            } else {
                state.loadVertragListError = action.payload
            }
        })

        builder.addCase(loadVertrag.pending, (state) => {
            state.loadVertragStatus = 'pending'
            state.loadVertragError = undefined
        })
        builder.addCase(loadVertrag.fulfilled, (state, action) => {
            state.loadVertragStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.vertragList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.vertragList.push(newItem) }
                else { state.vertragList[i] = { ...state.vertragList[i], ...newItem } }
            }
        })
        builder.addCase(loadVertrag.rejected, (state, action) => {
            state.loadVertragStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadVertragError = action.error.message
            } else {
                state.loadVertragError = action.payload
            }
        })

        builder.addCase(saveVertrag.pending, (state) => {
            state.saveVertragStatus = 'pending'
            state.saveVertragError = undefined
        })
        builder.addCase(saveVertrag.fulfilled, (state, action) => {
            state.saveVertragStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.vertragList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.vertragList.push(newItem) }
                else { state.vertragList[i] = { ...state.vertragList[i], ...newItem } }
            }
        })
        builder.addCase(saveVertrag.rejected, (state, action) => {
            state.saveVertragStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.saveVertragError = action.error.message
            } else {
                state.saveVertragError = action.payload
            }
        })

        builder.addCase(deleteVertrag.pending, (state) => {
            state.deleteVertragStatus = 'pending'
            state.deleteVertragError = undefined
        })
        builder.addCase(deleteVertrag.fulfilled, (state, action) => {
            state.deleteVertragStatus = 'idle'

            {
                if (Array.isArray(action.payload.data)) {
                    state.vertragList = state.vertragList.filter((x: any) => !action.payload.data.includes(x.HDR_ID))
                } else {
                    state.vertragList = state.vertragList.filter((x: any) => x.HDR_ID !== action.payload.data)
                }
            }
        })
        builder.addCase(deleteVertrag.rejected, (state, action) => {
            state.deleteVertragStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.deleteVertragError = action.error.message
            } else {
                state.deleteVertragError = action.payload
            }
        })

    }
})

export const {
    resetloadVertragListError,
    resetloadVertrag,
    resetloadVertragError,
    resetsaveVertrag,
    resetsaveVertragError,
    resetdeleteVertragError,
    removeFromVertragListById,
    removeFromVertragListByIds,
    setFilterValues,
    setFilterLookupValue,
    setDirectLoad,
    setLoad,
    setActiveDivRef,
    setFilterValuesId,
    setValuesFromDB,
    setFilterVisible,
    setFilterVisibleId,
    preloadData,
    clearPreloadedData,
} = VertragSlice.actions
export default VertragSlice.reducer
